<template>
  <div class="student-management-container">
    <el-card>
      <div slot="header">
        <h3>学员管理</h3>
      </div>

      <!-- 桌面端表格视图 -->
      <el-table :data="students" v-loading="loading" v-if="!isMobile">
        <el-table-column prop="studentName" label="学员姓名" width="120"></el-table-column>
        <el-table-column prop="phone" label="手机号" width="150"></el-table-column>
        <el-table-column prop="courseType" label="课程类型" width="100">
          <template slot-scope="scope">
            {{ scope.row.courseType === 'C1' ? 'C1手动挡' : 'C2自动挡' }}
          </template>
        </el-table-column>
        <el-table-column prop="totalHours" label="总学时" width="80"></el-table-column>
        <el-table-column prop="completedHours" label="已完成" width="80"></el-table-column>
        <el-table-column prop="currentSubject" label="当前科目" width="100">
          <template slot-scope="scope">
            {{ getSubjectText(scope.row.currentSubject) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200">
          <template slot-scope="scope">
            <el-button type="text" @click="viewProgress(scope.row)">学习进度</el-button>
            <el-button type="text" @click="viewAppointments(scope.row)">预约记录</el-button>
            <el-button type="text" @click="addRemark(scope.row)">添加备注</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 移动端卡片视图 -->
      <div v-if="isMobile" class="mobile-student-list" v-loading="loading">
        <div v-for="student in students" :key="student.id" class="student-card">
          <el-card shadow="hover">
            <div class="card-header">
              <div class="student-name">
                <h4>{{ student.studentName }}</h4>
                <el-tag size="mini" :type="student.courseType === 'C1' ? 'primary' : 'success'">
                  {{ student.courseType === 'C1' ? 'C1手动挡' : 'C2自动挡' }}
                </el-tag>
              </div>
              <div class="current-subject">
                <el-tag size="mini">
                  {{ getSubjectText(student.currentSubject) }}
                </el-tag>
              </div>
            </div>

            <div class="card-content">
              <div class="info-row">
                <span class="label">手机号:</span>
                <span class="value">{{ student.phone }}</span>
              </div>

              <div class="info-row">
                <span class="label">学时进度:</span>
                <span class="value">{{ student.completedHours }}/{{ student.totalHours }}小时</span>
              </div>

              <div class="progress-row">
                <el-progress
                    :percentage="Math.round((student.completedHours / student.totalHours) * 100)"
                    :show-text="false"
                    style="flex: 1;"
                ></el-progress>
                <span class="progress-text">
                  {{ Math.round((student.completedHours / student.totalHours) * 100) }}%
                </span>
              </div>
            </div>

            <div class="card-actions">
              <el-button type="text" size="small" @click="viewProgress(student)">学习进度</el-button>
              <el-button type="text" size="small" @click="viewAppointments(student)">预约记录</el-button>
              <el-button type="text" size="small" @click="addRemark(student)">添加备注</el-button>
            </div>
          </el-card>
        </div>
      </div>
    </el-card>

    <!-- 学习进度对话框 -->
    <el-dialog title="学员学习进度" :visible.sync="progressDialogVisible" width="600px" :fullscreen="isMobile">
      <student-progress :student-id="selectedStudentId" v-if="progressDialogVisible" />
    </el-dialog>

    <!-- 添加备注对话框 -->
    <el-dialog title="添加教学备注" :visible.sync="remarkDialogVisible" width="500px" :fullscreen="isMobile">
      <el-form :model="remarkForm" ref="remarkForm">
        <el-form-item label="备注内容">
          <el-input
              v-model="remarkForm.content"
              type="textarea"
              rows="4"
              placeholder="请输入教学备注..."
          ></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="remarkDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveRemark">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { coachAPI } from '@/services/api'
import StudentProgress from '@/components/StudentProgress.vue'

export default {
  name: 'StudentManagement',
  components: {
    StudentProgress
  },
  data() {
    return {
      loading: false,
      students: [],
      progressDialogVisible: false,
      remarkDialogVisible: false,
      selectedStudentId: null,
      isMobile: false,
      remarkForm: {
        content: ''
      }
    }
  },
  async mounted() {
    this.checkIsMobile()
    window.addEventListener('resize', this.checkIsMobile)
    await this.loadStudents()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkIsMobile)
  },
  methods: {
    checkIsMobile() {
      this.isMobile = window.innerWidth <= 768
    },

    async loadStudents() {
      this.loading = true
      try {
        const response = await coachAPI.getStudents()
        this.students = response.data
      } catch (error) {
        this.$message.error('加载学员列表失败')
      } finally {
        this.loading = false
      }
    },

    viewProgress(student) {
      this.selectedStudentId = student.id
      this.progressDialogVisible = true
    },

    viewAppointments(student) {
      this.$router.push({
        path: '/dashboard/coach-reservations',
        query: { studentId: student.id }
      })
    },

    addRemark(student) {
      this.selectedStudentId = student.id
      this.remarkForm.content = ''
      this.remarkDialogVisible = true
    },

    async saveRemark() {
      if (!this.remarkForm.content.trim()) {
        this.$message.warning('请输入备注内容')
        return
      }

      try {
        await coachAPI.addStudentRemark({
          studentId: this.selectedStudentId,
          content: this.remarkForm.content
        })
        this.$message.success('备注添加成功')
        this.remarkDialogVisible = false
      } catch (error) {
        this.$message.error('添加备注失败')
      }
    },

    getSubjectText(subject) {
      const subjects = {
        'SUBJECT1': '科目一',
        'SUBJECT2': '科目二',
        'SUBJECT3': '科目三',
        'SUBJECT4': '科目四'
      }
      return subjects[subject] || subject
    }
  }
}
</script>

<style scoped>
.student-management-container {
  padding: 20px;
}

/* 移动端卡片样式 */
.mobile-student-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.student-card {
  width: 100%;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.student-name {
  display: flex;
  align-items: center;
  gap: 8px;
}

.student-name h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.card-content {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;
}

.info-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.info-row .label {
  color: #606266;
  font-size: 14px;
  min-width: 80px;
}

.info-row .value {
  color: #303133;
  font-size: 14px;
  text-align: right;
  flex: 1;
}

.progress-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.progress-text {
  font-size: 12px;
  color: #909399;
  min-width: 40px;
}

.card-actions {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
}

.card-actions .el-button {
  flex: 1;
  min-width: 80px;
  font-size: 12px;
}

/* 移动端优化 */
@media (max-width: 768px) {
  .student-management-container {
    padding: 10px;
  }

  .student-name h4 {
    font-size: 15px;
  }

  .info-row .label,
  .info-row .value {
    font-size: 13px;
  }

  .card-actions .el-button {
    font-size: 11px;
    padding: 6px 8px;
  }

  /* 对话框全屏 */
  .el-dialog {
    width: 95% !important;
    margin: 5% auto !important;
  }

  .el-dialog__body {
    padding: 15px;
  }
}

@media (max-width: 480px) {
  .student-name h4 {
    font-size: 14px;
  }

  .info-row .label,
  .info-row .value {
    font-size: 12px;
  }

  .card-actions {
    flex-direction: column;
  }

  .card-actions .el-button {
    width: 100%;
  }
}
</style>
